import React, { useState, useEffect, useRef } from 'react';
import { User, LogOut, Settings, Menu, X, Plus, MessageSquare, ChevronDown, Send, Zap, Crown, Clock, Sparkles } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { ChevronUp } from 'react-bootstrap-icons';
import { FiSidebar } from 'react-icons/fi';

const OlewinApp = () => {
  const [currentScreen, setCurrentScreen] = useState('preloader');
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [chats, setChats] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [sessionTime, setSessionTime] = useState(5 * 60 * 60);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentScreen('login');
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (user && sessionTime > 0) {
      const interval = setInterval(() => {
        setSessionTime(prev => {
          if (prev <= 1) {
            handleLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [user, sessionTime]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      const mockUser = {
        uid: 'user_' + Date.now(),
        email: 'user@infiniteai.com',
        displayName: 'B Mahesh',
        photoURL: 'https://ui-avatars.com/api/?name=Alex+Morgan&background=667eea&color=fff&size=128'
      };
      
      setUser(mockUser);
      setCurrentScreen('home');
      setSessionTime(5 * 60 * 60);
      
      const savedChats = localStorage.getItem(`chats_${mockUser.uid}`);
      if (savedChats) {
        setChats(JSON.parse(savedChats));
      }
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleLogout = () => {
    setUser(null);
    setCurrentScreen('login');
    setChats([]);
    setMessages([]);
    setCurrentChatId(null);
    setSessionTime(5 * 60 * 60);
    setShowUserMenu(false);
  };

  const createNewChat = () => {
    const newChat = {
      id: Date.now().toString(),
      title: 'New Project',
      messages: [],
      createdAt: new Date().toISOString()
    };
    const updatedChats = [newChat, ...chats];
    setChats(updatedChats);
    setCurrentChatId(newChat.id);
    setMessages([]);
    saveChatsToStorage(updatedChats);
    
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  const selectChat = (chatId) => {
    const selectedChat = chats.find(c => c.id === chatId);
    setCurrentChatId(chatId);
    setMessages(selectedChat?.messages || []);
    
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isTyping) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue,
      timestamp: new Date().toISOString()
    };

    let newMessages = [...messages, userMessage];
    setMessages(newMessages);
    const currentInput = inputValue;
    setInputValue('');
    setIsTyping(true);

    if (!currentChatId) {
      const newChat = {
        id: Date.now().toString(),
        title: currentInput.substring(0, 40) + (currentInput.length > 40 ? '...' : ''),
        messages: newMessages,
        createdAt: new Date().toISOString()
      };
      const updatedChats = [newChat, ...chats];
      setChats(updatedChats);
      setCurrentChatId(newChat.id);
      saveChatsToStorage(updatedChats);
    }

    setTimeout(() => {
      const aiMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `I understand you want to build: "${currentInput}"\n\nI'm the Infinite Super Intelligence AI, capable of creating complete production-ready applications from a single prompt. I can develop:\n\n✓ Full-stack web applications (Static & Dynamic)\n✓ Mobile apps (iOS & Android)\n✓ Enterprise systems (ERM, CRM, LMS)\n✓ Automated workflows and integrations\n✓ Scalable cloud architectures\n\nI'll guide you through the entire development process with infinite iterations until perfection. Let's start building your vision into reality!`,
        timestamp: new Date().toISOString()
      };
      
      newMessages = [...newMessages, aiMessage];
      setMessages(newMessages);
      setIsTyping(false);
      
      if (currentChatId) {
        const updatedChats = chats.map(chat => 
          chat.id === currentChatId 
            ? { ...chat, messages: newMessages }
            : chat
        );
        setChats(updatedChats);
        saveChatsToStorage(updatedChats);
      }
    }, 2000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const saveChatsToStorage = (chatsData) => {
    if (user) {
      localStorage.setItem(`chats_${user.uid}`, JSON.stringify(chatsData));
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  if (currentScreen === 'preloader') {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100" style={{ background: 'linear-gradient(135deg, #089 0%, #58A95B 100%)' }}>
        <div className="text-center">
        <div className="mb-3">
        <img
        src="https://cdn.jsdelivr.net/gh/OlewinAcademy/Academy_brochures@main/olewin_in.svg"
          alt="Olewin Logo"
          width="70"
          height="70"
          style={{
            filter: 'drop-shadow(0 4px 6px rgba(102, 126, 234, 0.3))',
          }}
          />
          </div>

          <h1 className="text-dark fw-bold display-3 mb-3">Olewin AI</h1>
          <p className="text-dark">Loading your Infinite SuperIntelligence Model...</p>
          <div className="spinner-border text-dark mt-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (currentScreen === 'login') {
    return (
      <div className="container-fluid vh-100 p-0 overflow-hidden">
        <div className="row g-0 h-100">
          <div className="col-lg-6 d-none d-lg-flex flex-column justify-content-between p-5" 
               style={{ background: 'linear-gradient(135deg, #008899 0%, #58A95B 100%)' }}>
            <div className="flex-grow-1 d-flex flex-column justify-content-center">
              <TypewriterText
                text="Infinite Super Intelligence Model"
                className="display-4 fw-bold text-white mb-4"
                speed={50}
              />
              <TypewriterText
                text="Create complete prompt-to-production ready applications for startups, mid-Fortune, and enterprise level systems. Build static, dynamic, and mobile apps for both Android and iOS, including ERM, CRM, and LMS systems. Everything with an infinite loop from a single prompt - complete automation at your fingertips."
                className="lead text-white mb-4"
                delay={2500}
                speed={30}
              />
            </div>
            <div className="text-white small">
              <a href="/" className="text-white text-decoration-none me-3 opacity-75">Terms & Conditions</a>
              <a href="/" className="text-white text-decoration-none me-3 opacity-75">Privacy Policy</a>
              <span className="opacity-75">© 2025 Olewin AI. All rights reserved.</span>
            </div>
          </div>

          <div className="col-lg-6 d-flex align-items-center justify-content-center bg-light p-4">
            <div style={{ maxWidth: '420px', width: '100%' }}>
              <div className="text-center ">
               <div className="mb-1">
               <img
               src="https://cdn.jsdelivr.net/gh/OlewinAcademy/Academy_brochures@main/olewin_in.svg"
                 alt="Olewin Logo"
                 width="70"
                 height="70"
                 style={{
                   filter: 'drop-shadow(0 4px 6px rgba(102, 126, 234, 0.3))',
                 }}
                 />
                 </div>

                <h2 className="fw-bold mb-2">Welcome to Olewin AI</h2>
                <p className="text-muted">Sign in to unlock infinite possibilities</p>
              </div>

              <div className="card border-0 shadow-lg">
                <div className="card-body p-4">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Email Address</label>
                    <input 
                      type="email" 
                      className="form-control form-control-lg" 
                      placeholder="Enter your email"
                      style={{ borderRadius: '10px' }}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="form-label fw-semibold">Password</label>
                    <input 
                      type="password" 
                      className="form-control form-control-lg" 
                      placeholder="Enter your password"
                      style={{ borderRadius: '10px' }}
                    />
                  </div>
                  
                  <div className="text-center mb-3">
                    <span className="text-muted small">or</span>
                  </div>

                  <button 
                    className="btn btn-lg w-100 d-flex align-items-center justify-content-center shadow-sm"
                    onClick={handleGoogleSignIn}
                    style={{ 
                      borderRadius: '10px',
                      border: '2px solid #e0e0e0',
                      background: 'white',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                  >
                    <svg className="me-2" width="20" height="20" viewBox="0 0 20 20">
                      <path fill="#4285F4" d="M18.3 8.2H9.6v3.8h5c-.4 2.1-2.1 3.7-4.4 3.7-2.7 0-4.9-2.2-4.9-4.9s2.2-4.9 4.9-4.9c1.2 0 2.3.4 3.1 1.2l2.8-2.8C14.2 2.5 12 1.5 9.6 1.5c-4.7 0-8.5 3.8-8.5 8.5s3.8 8.5 8.5 8.5 8.5-3.8 8.5-8.5c0-.6 0-1.2-.2-1.8z"/>
                      <path fill="#34A853" d="M2.6 6.1l3.2 2.4c.8-1.9 2.7-3.2 4.8-3.2 1.2 0 2.3.4 3.1 1.2l2.8-2.8C14.6 2 12.4 1 9.6 1 6.2 1 3.3 3.1 2.6 6.1z"/>
                      <path fill="#FBBC05" d="M9.6 18.5c2.7 0 5-1 6.7-2.7l-3.1-2.6c-.9.6-2 1-3.6 1-2.3 0-4.3-1.5-5-3.6l-3.2 2.5c1.4 2.8 4.3 4.9 7.2 5.4z"/>
                      <path fill="#EA4335" d="M18.3 8.2H9.6v3.8h5c-.2 1-.8 1.9-1.6 2.5l3.1 2.6c1.8-1.7 2.9-4.2 2.9-7.1 0-.6 0-1.2-.2-1.8z"/>
                    </svg>
                    <span className="fw-semibold text-dark">Continue with Google</span>
                  </button>

                  <p className="text-center text-muted small mt-4 mb-0">
                    By signing in, you agree to our Terms of Service and Privacy Policy
                  </p>
                </div>
              </div>

              <div className="text-center mt-4 d-lg-none">
                <small className="text-muted">
                  <a href="/" className="text-decoration-none text-muted me-2">Terms</a>
                  <a href="/" className="text-decoration-none text-muted me-2">Privacy</a>
                  <span>© 2025 Olewin AI - SuperIntelligence Agentic Model</span>
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex vh-100 bg-light mt-5 py-5 ">
      {window.innerWidth <= 768 && sidebarOpen && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 bg-dark"
          style={{ opacity: 0.5, zIndex: 999 }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div 
        className={`bg-dark text-white position-relative ${window.innerWidth <= 768 ? 'position-relative' : ''}`}
        style={{
          width: sidebarOpen ? '280px' : '0',
          transition: 'width 0.3s ease',
          overflow: 'hidden',
          height: '100vh',
          zIndex: window.innerWidth <= 768 ? 1000 : 1
        }}
      >
        <div className="d-flex flex-column h-100 py-5 my-5" style={{ width: '280px' }}>
          <div className="p-3 border-bottom border-secondary">
            <button 
              className="btn btn-success w-100 d-flex align-items-center justify-content-center shadow-sm"
              onClick={createNewChat}
              style={{ borderRadius: '10px', fontSize: '15px', padding: '12px' }}
            >
              <Plus size={20} className="me-2" />
              New Project
            </button>
          </div>

          <div className="flex-grow-1 overflow-auto p-2" style={{ scrollbarWidth: 'thin' }}>
            {chats.length === 0 ? (
              <div className="text-center text-muted p-4">
                <MessageSquare size={40} className="mb-2 opacity-50" />
                <p className="small">No projects yet. Start a new one!</p>
              </div>
            ) : (
              chats.map(chat => (
                <div
                  key={chat.id}
                  className={`p-3 rounded mb-2 cursor-pointer ${currentChatId === chat.id ? 'bg-success' : 'bg-secondary bg-opacity-10'}`}
                  onClick={() => selectChat(chat.id)}
                  style={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    borderRadius: '10px'
                  }}
                  onMouseEnter={(e) => {
                    if (currentChatId !== chat.id) {
                      e.target.style.backgroundColor = 'rgba(255,255,255,0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentChatId !== chat.id) {
                      e.target.style.backgroundColor = 'rgba(255,255,255,0.05)';
                    }
                  }}
                >
                  <div className="d-flex align-items-start">
                    <MessageSquare size={18} className="me-2 mt-1 flex-shrink-0" />
                    <span className="small text-truncate" style={{ fontSize: '14px' }}>
                      {chat.title || 'New Project'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="border-top border-secondary p-3">
            <div className="position-relative">
              <div 
                className="d-flex align-items-center cursor-pointer p-2 rounded"
                onClick={() => setShowUserMenu(!showUserMenu)}
                style={{ 
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                  background: showUserMenu ? 'rgba(255,255,255,0.1)' : 'transparent'
                }}
              >
                <img 
                  src={user?.photoURL} 
                  alt="User" 
                  className="rounded-circle me-2"
                  style={{ width: '38px', height: '38px', border: '2px solid rgba(255,255,255,0.2)' }}
                />
                <div className="flex-grow-1">
                  <div className="small fw-bold text-truncate" style={{ fontSize: '14px' }}>
                    {user?.displayName}
                  </div>
                  <div className="d-flex align-items-center" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>
                    <Clock size={12} className="me-1" />
                    {formatTime(sessionTime)} remaining
                  </div>
                </div>
                <ChevronUp size={18} style={{ 
                  transform: showUserMenu ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.3s ease'
                }} />
              </div>

              {showUserMenu && (
                <div 
                  className="position-absolute bottom-100 start-0 mb-2 bg-white text-dark rounded shadow-lg"
                  style={{ width: '100%', zIndex: 2000, borderRadius: '12px', overflow: 'hidden' }}
                >
                  <div className="p-2">
                    <button 
                      className="btn btn-sm btn-light w-100 text-start mb-1 d-flex align-items-center"
                      onClick={() => {
                        setShowUpgradeModal(true);
                        setShowUserMenu(false);
                      }}
                      style={{ borderRadius: '8px', padding: '10px' }}
                    >
                      <Crown size={16} className="me-2 text-warning" />
                      <span style={{ fontSize: '14px' }}>Upgrade Plan</span>
                    </button>
                    <button 
                      className="btn btn-sm btn-light w-100 text-start mb-1 d-flex align-items-center"
                      style={{ borderRadius: '8px', padding: '10px' }}
                    >
                      <Settings size={16} className="me-2" />
                      <span style={{ fontSize: '14px' }}>Settings</span>
                    </button>
                    <hr className="my-2" style={{ margin: '8px 0' }} />
                    <button 
                      className="btn btn-sm btn-danger w-100 text-start d-flex align-items-center"
                      onClick={handleLogout}
                      style={{ borderRadius: '8px', padding: '10px' }}
                    >
                      <LogOut size={16} className="me-2" />
                      <span style={{ fontSize: '14px' }}>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-grow-1 d-flex flex-column" style={{ minWidth: 0 }}>
        <nav className="navbar bg-white border-bottom shadow-sm px-3 py-2">
          <div className="d-flex align-items-center w-100">
            <button 
              className="btn btn-link text-dark p-0 me-3"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{ textDecoration: 'none' }}
            >
              <FiSidebar  size={25} />
            </button>
            <div className="d-flex align-items-center">
              <span className="fw-bold me-3">Olewin AI </span>
            </div>
            <div className="ms-auto d-none d-md-flex align-items-center">
              <div className="badge bg-primary bg-opacity-10 text-primary me-3 px-3 py-2">
                <Clock size={14} className="me-1" />
                {formatTime(sessionTime)}
              </div>
              <img 
                src={user?.photoURL}
                alt="User"
                className="rounded-circle"
                style={{ width: '38px', height: '38px', cursor: 'pointer', border: '2px solid #e0e0e0' }}
                onClick={() => setShowUserMenu(!showUserMenu)}
              />
            </div>
          </div>
        </nav>

        <div className="flex-grow-1 overflow-auto p-3" style={{ background: '#f8f9fa' }}>
          {messages.length === 0 ? (
            <div className="d-flex flex-column justify-content-center align-items-center h-100 text-center px-3">
              <div className="mb-4" style={{ 
                animation: 'float 3s ease-in-out infinite'
              }}>
                <Zap size={80} className="text-primary" style={{ 
                  filter: 'drop-shadow(0 4px 12px rgba(102, 126, 234, 0.3))'
                }} />
              </div>
              <h2 className="fw-bold mb-3">{getGreeting()}, {user?.displayName?.split(' ')[0]}!</h2>
              <p className="text-muted mb-5" style={{ maxWidth: '600px' }}>
                I'm your Infinite Super Intelligence AI. I can transform your ideas into production-ready applications with complete automation. What would you like to create today?
              </p>
              <div className="row g-3" style={{ maxWidth: '900px', width: '100%' }}>
                {[
                  { icon: '🚀', text: 'Build a full-stack e-commerce platform with payment integration' },
                  { icon: '📊', text: 'Create an enterprise CRM with analytics dashboard' },
                  { icon: '📱', text: 'Develop a cross-platform mobile app for iOS and Android' },
                  { icon: '🎓', text: 'Generate a complete LMS with course management' }
                ].map((prompt, idx) => (
                  <div key={idx} className="col-md-6">
                    <div 
                      className="card h-100 border-0 shadow-sm"
                      onClick={() => setInputValue(prompt.text)}
                      style={{ 
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        borderRadius: '12px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                      }}
                    >
                      <div className="card-body p-4">
                        <div className="fs-3 mb-2">{prompt.icon}</div>
                        <p className="mb-0 small">{prompt.text}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
              {messages.map(msg => (
                <div key={msg.id} className={`mb-4 ${msg.role === 'user' ? 'd-flex justify-content-end' : ''}`}>
                  <div 
                    className={`p-3 ${msg.role === 'user' ? 'bg-primary text-white' : 'bg-white border'}`}
                    style={{ 
                      maxWidth: '85%',
                      borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    {msg.content}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="mb-4">
                  <div className="p-3 bg-white border" style={{ 
                    maxWidth: '85%',
                    borderRadius: '18px 18px 18px 4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  }}>
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        <div className="border-top bg-white p-3 shadow-sm">
          <div className="container" style={{ maxWidth: '900px' }}>
            <div className="input-group shadow-sm" style={{ borderRadius: '12px', overflow: 'hidden' }}>
              <input
                type="text"
                className="form-control border-0"
                placeholder="Describe your application idea in detail..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isTyping}
                style={{ 
                  padding: '14px 20px',
                  fontSize: '15px'
                }}
              />
              <button 
                className="btn btn-primary border-0 px-4"
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isTyping}
                style={{ 
                  transition: 'all 0.3s ease'
                }}
              >
                <Send size={20} />
              </button>
            </div>
            <p className="text-center text-muted small mt-2 mb-0">
              Infinite AI can make mistakes. Verify important information.
            </p>
          </div>
        </div>
      </div>

      {showUpgradeModal && (
        <div 
          className="modal show d-block" 
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={() => setShowUpgradeModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px' }}>
              <div className="modal-header border-0 pb-0">
                <h4 className="modal-title fw-bold">Upgrade Your Plan</h4>
                <button 
                  className="btn-close"
                  onClick={() => setShowUpgradeModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4">
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="card h-100 border-2 shadow-sm" style={{ borderRadius: '16px' }}>
                      <div className="card-body p-4 text-center">
                        <div className="badge bg-primary bg-opacity-10 text-primary mb-3 px-3 py-2">
                          Popular
                        </div>
                        <h3 className="fw-bold mb-2">Pro</h3>
                        <div className="mb-4">
                          <span className="display-4 fw-bold text-primary">$29</span>
                          <span className="text-muted">/month</span>
                        </div>
                        <ul className="list-unstyled text-start mb-4">
                          <li className="mb-3 d-flex align-items-start">
                            <span className="text-primary me-2">✓</span>
                            <span>24/7 Priority support</span>
                          </li>
                          <li className="mb-3 d-flex align-items-start">
                            <span className="text-primary me-2">✓</span>
                            <span>Advanced AI models (GPT-4, Claude)</span>
                          </li>
                          <li className="mb-3 d-flex align-items-start">
                            <span className="text-primary me-2">✓</span>
                            <span>Priority processing queue</span>
                          </li>
                          <li className="mb-3 d-flex align-items-start">
                            <span className="text-primary me-2">✓</span>
                            <span>Export to GitHub integration</span>
                          </li>
                          <li className="mb-3 d-flex align-items-start">
                            <span className="text-primary me-2">✓</span>
                            <span>Unlimited session time</span>
                          </li>
                        </ul>
                        <button className="btn btn-primary w-100 py-3 fw-semibold" style={{ borderRadius: '12px' }}>
                          Upgrade to Pro
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card h-100 border-primary border-3 shadow" style={{ borderRadius: '16px' }}>
                      <div className="card-body p-4 text-center position-relative">
                        <div className="badge bg-warning text-dark mb-3 px-3 py-2">
                          <Crown size={14} className="me-1" />
                          Best Value
                        </div>
                        <h3 className="fw-bold mb-2">Enterprise</h3>
                        <div className="mb-4">
                          <span className="display-4 fw-bold text-primary">$99</span>
                          <span className="text-muted">/month</span>
                        </div>
                        <ul className="list-unstyled text-start mb-4">
                          <li className="mb-3 d-flex align-items-start">
                            <span className="text-primary me-2">✓</span>
                            <span><strong>Everything in Pro, plus:</strong></span>
                          </li>
                          <li className="mb-3 d-flex align-items-start">
                            <span className="text-primary me-2">✓</span>
                            <span>Custom AI model training</span>
                          </li>
                          <li className="mb-3 d-flex align-items-start">
                            <span className="text-primary me-2">✓</span>
                            <span>Dedicated account manager</span>
                          </li>
                          <li className="mb-3 d-flex align-items-start">
                            <span className="text-primary me-2">✓</span>
                            <span>White-label solution</span>
                          </li>
                          <li className="mb-3 d-flex align-items-start">
                            <span className="text-primary me-2">✓</span>
                            <span>API access for integration</span>
                          </li>
                          <li className="mb-3 d-flex align-items-start">
                            <span className="text-primary me-2">✓</span>
                            <span>SLA guarantee & compliance</span>
                          </li>
                        </ul>
                        <button className="btn btn-primary w-100 py-3 fw-semibold" style={{ borderRadius: '12px' }}>
                          Upgrade to Enterprise
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-center text-muted small mt-4 mb-0">
                  All plans include 14-day money-back guarantee • Cancel anytime
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        .typing-indicator {
          display: flex;
          gap: 4px;
          align-items: center;
        }
        
        .typing-indicator span {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #667eea;
          animation: typing 1.4s infinite;
        }
        
        .typing-indicator span:nth-child(2) {
          animation-delay: 0.2s;
        }
        
        .typing-indicator span:nth-child(3) {
          animation-delay: 0.4s;
        }
        
        @keyframes typing {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-10px); opacity: 1; }
        }
        
        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        
        ::-webkit-scrollbar-track {
          background: transparent;
        }
        
        ::-webkit-scrollbar-thumb {
          background: rgba(0,0,0,0.2);
          border-radius: 10px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(0,0,0,0.3);
        }
        
        .cursor-pointer {
          cursor: pointer;
        }
        
        @media (max-width: 768px) {
          .navbar {
            padding: 12px 16px !important;
          }
        }
      `}</style>
    </div>
  );
};

const TypewriterText = ({ text, className, delay = 0, speed = 50 }) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isStarted, setIsStarted] = useState(false);

  useEffect(() => {
    const startTimer = setTimeout(() => {
      setIsStarted(true);
    }, delay);
    return () => clearTimeout(startTimer);
  }, [delay]);

  useEffect(() => {
    if (isStarted && currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, text, speed, isStarted]);

  return (
    <div className={className}>
      {displayText}
      {currentIndex < text.length && (
        <span style={{ 
          animation: 'blink 1s infinite',
          display: 'inline-block',
          width: '2px',
          height: '1em',
          backgroundColor: 'currentColor',
          marginLeft: '2px',
          verticalAlign: 'text-bottom'
        }}>|</span>
      )}
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default OlewinApp;